import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { LogOut as LogOutIcon } from 'react-feather';
import { HOME_TOP_BAR_BG } from 'src/layouts/HomeLayout/TopBar';

const ACCENT = '#009eda';
const TITLE_COLOR = '#0F172A';
const BODY_COLOR = '#64748B';

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'relative',
    width: '92%',
    maxWidth: 520,
    margin: 16,
    borderRadius: 12,
    borderTop: `6px solid ${ACCENT}`,
    backgroundColor: '#ffffff',
    padding: '22px 18px 20px',
    boxSizing: 'border-box',
    overflow: 'visible',
    [theme.breakpoints.up('md')]: {
      padding: '28px 26px 26px',
    },
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 34,
    height: 34,
    padding: 0,
    backgroundColor: '#EEF1F5',
    color: '#475569',
    '&:hover': {
      backgroundColor: '#E2E8F0',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: HOME_TOP_BAR_BG,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      width: 76,
      height: 76,
    },
  },
  title: {
    marginTop: 14,
    color: TITLE_COLOR,
    fontFamily: 'Helvetica',
    fontWeight: 700,
    fontSize: 26,
    lineHeight: 1.2,
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
    },
  },
  message: {
    marginTop: 8,
    maxWidth: 440,
    color: BODY_COLOR,
    fontFamily: 'Helvetica',
    fontSize: 15,
    lineHeight: 1.5,
    [theme.breakpoints.up('md')]: {
      fontSize: 17,
    },
  },
  actions: {
    display: 'flex',
    width: '100%',
    marginTop: 24,
  },
  actionButton: {
    flex: 1,
    height: 48,
    borderRadius: 8,
    fontFamily: 'Helvetica',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    [theme.breakpoints.up('md')]: {
      fontSize: 17,
    },
  },
  signOutButton: {
    marginRight: 12,
    backgroundColor: '#ffffff',
    color: HOME_TOP_BAR_BG,
    border: '1px solid #D5DBE5',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
  },
  cancelButton: {
    backgroundColor: ACCENT,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: ACCENT,
    },
  },
}));

function SignOutDialog({ open, onClose, onConfirm }) {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}
      PaperProps={{ className: classes.paper }}
      aria-labelledby="sign-out-dialog-title"
    >
      <IconButton className={classes.closeButton} onClick={onClose} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box className={classes.body}>
        <Box className={classes.iconCircle} aria-hidden>
          <LogOutIcon size={30} />
        </Box>
        <Typography id="sign-out-dialog-title" component="h2" className={classes.title}>
          Sign out?
        </Typography>
        <Typography className={classes.message}>
          You’ll need to sign in again to view live inventory and bulk pricing.
        </Typography>
        <Box className={classes.actions}>
          <Button
            className={`${classes.actionButton} ${classes.signOutButton}`}
            onClick={onConfirm}
          >
            Sign out
          </Button>
          <Button
            className={`${classes.actionButton} ${classes.cancelButton}`}
            onClick={onClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

SignOutDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

SignOutDialog.defaultProps = {
  open: false,
};

export default SignOutDialog;
